import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { concatAll, takeUntil } from 'rxjs/operators';

import { ChartTypes } from '@site-mate/dashpivot-shared-library';

import { AnalyticsService } from 'app/analytics/analytics.service';
import { ChartUtilsService } from 'app/shared/service/chart-utils.service';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-dashboards-grid',
  templateUrl: './dashboards-grid.component.html',
  styleUrls: ['../../../../styles/components/tool-bar.component.scss', './dashboards-grid.component.scss'],
})
export class DashboardsGridComponent implements OnInit, OnDestroy {
  readonly chartsSampleSize = 4;
  readonly chartTypes = ChartTypes;
  dashboards;
  templates;
  selectedDashboards = new Map();
  teamId;
  companyId;
  canAdd = false;
  unSubscribe = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly routeParamsService: RouteParamsService,
    private readonly confirmService: ConfirmService,
    private readonly analyticsService: AnalyticsService,
    private readonly toastrService: ToastrService,
    private readonly errorHandler: ErrorHandler,
    private readonly userService: UserService,
    public readonly chartUtilsService: ChartUtilsService,
  ) {}

  ngOnInit() {
    this.teamId = this.route.snapshot.paramMap.get('teamId');
    this.companyId = this.route.snapshot.paramMap.get('companyId');
    this.routeParamsService
      .get('dashboards')
      .pipe(takeUntil(this.unSubscribe), concatAll())
      .subscribe((dashboards: any) => {
        this.dashboards = dashboards.sort((a, b) => a.name.localeCompare(b.name));
      });
    this.routeParamsService
      .get('templates')
      .pipe(takeUntil(this.unSubscribe), concatAll())
      .subscribe((templates) => {
        this.templates = templates;
        this.canAdd = this.userService.isTeamControllerOrAbove();
      });
  }

  onDashboardClick(dashboardId) {
    void this.router.navigate(['..', dashboardId, 'charts'], { relativeTo: this.route });
  }

  onSelectionToggle(dashboard, selected) {
    if (selected) {
      this.selectedDashboards.set(dashboard._id, dashboard);
    } else {
      this.selectedDashboards.delete(dashboard._id);
    }
  }

  deleteSelectedDashboards() {
    void this.confirmService
      .confirmDelete('deleteDashboard')
      .then(async () => {
        try {
          const promises = [];
          this.selectedDashboards.forEach((dashboard, id) => {
            let promise;
            if (this.teamId) {
              promise = this.analyticsService.deleteTeamDashboard(this.teamId, id).toPromise();
            } else {
              promise = this.analyticsService.deleteCompanyDashboard(this.companyId, id).toPromise();
            }
            promises.push(promise);
          });
          await Promise.all(promises);
          this.toastrService.successByKey('dashboardDeleted');
        } catch (e) {
          this.errorHandler.handle(e);
        }
        this.clearSelection();
        void this.router.navigate([], { relativeTo: this.route, state: { reload: true } });
        return true;
      })
      .catch(() => {
        // no-op
      });
  }

  clearSelection() {
    this.selectedDashboards.clear();
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
